import { useState } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import {
  Box,
  ThemeProvider,
  createTheme,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { Sidebar, Dashboard, ProtectedRoute } from './components'
import { AuthProvider } from './auth'
import { LoginPage } from './components/auth/LoginPage'
import { useInitializeParametricData } from './hooks/useInitializeParametricData'
import ErrorBoundary from './components/common/ErrorBoundary'
import ErrorNotification from './components/common/ErrorNotification'

// Feature pages
import ArticulosPage from './features/catalogo/articulos/ArticulosPage'
import MarcasPage from './features/catalogo/marcas/MarcasPage'
import ProveedoresPage from './features/catalogo/proveedores/ProveedoresPage'
import AsociacionPage from './features/catalogo/asociacion/AsociacionPage'
import EspeciesPage from './features/catalogo/especies/EspeciesPage'
import DueniosPage from './features/catalogo/duenios/DueniosPage'
import EncargadosPage from './features/catalogo/encargados/EncargadosPage'
import ProcedenciasPage from './features/catalogo/procedencias/ProcedenciasPage'
import DERCPage from './features/produccion/derc/DERCPage'
import GuiaMateriaPage from './features/produccion/guias-materia-prima/GuiaMateriaPage'
import GuiaMateriaFormPage from './features/produccion/guias-materia-prima/GuiaMateriaFormPage'
import GuiasProduccionPage from './features/produccion/guias-produccion/GuiasProduccionPage'
import GuiaProduccionFormPage from './features/produccion/guias-produccion/GuiaProduccionFormPage'
import ParteProduccionPage from './features/produccion/partes-produccion/ParteProduccionPage'
import ParteProduccionFormPage from './features/produccion/partes-produccion/ParteProduccionFormPage'
import DevolucionesInternasPage from './features/produccion/devoluciones-internas/DevolucionesInternasPage'
import ProductosTipoBPage from './features/produccion/productos-tipo-b/ProductosTipoBPage'
import OrdenLiquidacionPage from './features/produccion/orden-liquidacion/components/OrdenLiquidacionPage'
import OrdenLiquidacionFormPage from './features/produccion/orden-liquidacion/components/OrdenLiquidacionFormPage'
import GuiaSalidaPage from './features/produccion/guias-salida/components/GuiaSalidaPage'
import GuiaSalidaFormPage from './features/produccion/guias-salida/components/GuiaSalidaFormPage'
import PreciosProveedorPage from './features/produccion/precios-proveedor/components/PreciosProveedorPage'
import OrdenFacturacionPage from './features/produccion/orden-facturacion/components/OrdenFacturacionPage'
import OrdenFacturacionFormPage from './features/produccion/orden-facturacion/components/OrdenFacturacionFormPage'
import PreciosClientePage from './features/produccion/precios-cliente/components/PreciosClientePage'
import ReportsPage from './features/reports/ReportsPage'
import SettingsPage from './features/settings/SettingsPage'
import UsuariosPage from './features/seguridad/usuarios/components/UsuariosPage'
import RolesPage from './features/seguridad/usuarios/components/RolesPage'
import PermisosPage from './features/seguridad/usuarios/components/PermisosPage'

const createAppTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: '#3b82f6',
    },
    secondary: {
      main: '#10b981',
    },
    background: {
      default: mode === 'light' ? '#f9fafb' : '#111827',
      paper: mode === 'light' ? '#ffffff' : '#1f2937',
    },
    text: {
      primary: mode === 'light' ? '#1e293b' : '#f1f5f9',
      secondary: mode === 'light' ? '#64748b' : '#94a3b8',
    },
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, system-ui, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: mode === 'light' ? '#f9fafb' : '#111827',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
          color: mode === 'light' ? '#1e293b' : '#f1f5f9',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#ffffff' : '#1f2937',
          color: mode === 'light' ? '#1e293b' : '#f1f5f9',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
})

const AppContent: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const location = useLocation()

  // Initialize parametric data
  useInitializeParametricData()

  const theme = createAppTheme(darkMode ? 'dark' : 'light')

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleThemeToggle = () => {
    setDarkMode(!darkMode)
  }

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary
        resetOnPropsChange
        resetKeys={[location.pathname]}
      >
        <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
          <Sidebar
            mobileOpen={mobileOpen}
            onMobileToggle={handleDrawerToggle}
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
            darkMode={darkMode}
            onThemeToggle={handleThemeToggle}
          />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minHeight: '100vh',
              backgroundColor: '#f9fafb',
              overflowX: 'auto',
            }}
          >
            {/* Mobile AppBar */}
            <AppBar
              position="sticky"
              elevation={1}
              sx={{
                display: { xs: 'block', md: 'none' },
                backgroundColor: '#1e293b',
                color: '#ffffff',
              }}
            >
              <Toolbar sx={{ minHeight: 48 }}>
                <IconButton
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2, color: '#ffffff' }}
                  aria-label="abrir menú"
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="subtitle1" noWrap component="div" sx={{ fontWeight: 600, color: '#ffffff' }}>
                  CPP
                </Typography>
              </Toolbar>
            </AppBar>
            <ErrorBoundary resetOnPropsChange resetKeys={[location.pathname]}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/catalogo/articulos" element={<ArticulosPage />} />
                <Route path="/catalogo/marcas" element={<MarcasPage />} />
                <Route path="/catalogo/proveedores" element={<ProveedoresPage />} />
                <Route path="/catalogo/asociacion" element={<AsociacionPage />} />
                <Route path="/catalogo/especies" element={<EspeciesPage />} />
                <Route path="/catalogo/duenios" element={<DueniosPage />} />
                <Route path="/catalogo/encargados" element={<EncargadosPage />} />
                <Route path="/catalogo/procedencias" element={<ProcedenciasPage />} />
                <Route path="/produccion/derc" element={<DERCPage />} />
                <Route path="/produccion/guias-materia-prima" element={<GuiaMateriaPage />} />
                <Route path="/produccion/guias-materia-prima/:id" element={<GuiaMateriaFormPage />} />
                <Route path="/produccion/guias-produccion" element={<GuiasProduccionPage />} />
                <Route path="/produccion/guias-produccion/:id" element={<GuiaProduccionFormPage />} />
                <Route path="/produccion/partes-produccion" element={<ParteProduccionPage />} />
                <Route path="/produccion/partes-produccion/:id" element={<ParteProduccionFormPage />} />
                <Route path="/produccion/devoluciones-internas" element={<DevolucionesInternasPage />} />
                <Route path="/produccion/productos-tipo-b" element={<ProductosTipoBPage />} />
                <Route path="/produccion/guias-salida" element={<GuiaSalidaPage />} />
                <Route path="/produccion/guias-salida/nueva" element={<GuiaSalidaFormPage />} />
                <Route path="/produccion/guias-salida/:id" element={<GuiaSalidaFormPage />} />
                <Route path="/administracion/orden-liquidacion" element={<OrdenLiquidacionPage />} />
                <Route path="/administracion/orden-liquidacion/nueva" element={<OrdenLiquidacionFormPage />} />
                <Route path="/administracion/precios-proveedor" element={<PreciosProveedorPage />} />
                <Route path="/administracion/orden-facturacion" element={<OrdenFacturacionPage />} />
                <Route path="/administracion/orden-facturacion/nueva" element={<OrdenFacturacionFormPage />} />
                <Route path="/administracion/precios-cliente" element={<PreciosClientePage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/seguridad/usuarios" element={<UsuariosPage />} />
                <Route path="/seguridad/roles" element={<RolesPage />} />
                <Route path="/seguridad/permisos" element={<PermisosPage />} />
              </Routes>
            </ErrorBoundary>
          </Box>
        </Box>
        <ErrorNotification />
      </ErrorBoundary>
    </ThemeProvider>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <ErrorBoundary resetOnPropsChange>
            <Routes>
              <Route path="/login" element={
                <ErrorBoundary>
                  <LoginPage />
                </ErrorBoundary>
              } />
              <Route path="/*" element={
                <ProtectedRoute>
                  <AppContent />
                </ProtectedRoute>
              } />
            </Routes>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
